'use client';

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/utils/api';

/** Un giorno della serie di scostamento previsto/reale. */
export interface AccuracyDay {
	day: string;
	predicted: number;
	actual: number;
	biasPct: number;
	meters: number;
}

/** Riga del log di audit: un misuratore nella finestra osservata. */
export interface AccuracyMeter {
	meterId: string;
	codice: string;
	tipo: string;
	predicted: number;
	actual: number;
	biasPct: number;
	maeKwh: number;
	accuracyPct: number;
	days: number;
	hours: number;
}

export interface PredictiveAccuracy {
	windowDays: number;
	samples: number;
	meters: number;
	days: number;
	hours: number;
	predictedKwh: number;
	actualKwh: number;
	biasPct: number | null;
	maeKwh: number | null;
	rmseKwh: number | null;
	accuracyPct: number | null;
	daily: AccuracyDay[];
	perMeter: AccuracyMeter[];
}

const EMPTY: PredictiveAccuracy = {
	windowDays: 30,
	samples: 0,
	meters: 0,
	days: 0,
	hours: 0,
	predictedKwh: 0,
	actualKwh: 0,
	biasPct: null,
	maeKwh: null,
	rmseKwh: null,
	accuracyPct: null,
	daily: [],
	perMeter: [],
};

/**
 * Metriche di accuratezza del motore predittivo (INB-38).
 *
 * Non c'è realtime: le coppie previsto/reale si formano quando arriva il dato di
 * settlement GSE o la bolletta, cioè con cadenza mensile. Un canale che ascolta
 * `energy_readings` scatterebbe a ogni run predittivo dei 15 minuti, ricaricando
 * metriche che non possono essere cambiate.
 *
 * ADR-006: backed by React Query.
 */
export function usePredictiveAccuracy(
	orgId: string | null | undefined,
	windowDays = 30,
): { accuracy: PredictiveAccuracy; isLoading: boolean; error: string | null } {
	const { data, isLoading, error } = useQuery({
		queryKey: ['predictive-accuracy', orgId, windowDays],
		enabled: !!orgId,
		queryFn: () =>
			api.get<PredictiveAccuracy>('/api/energy/predictive-accuracy', {
				orgId: orgId as string,
				days: windowDays,
			}),
	});

	return {
		accuracy: data ?? EMPTY,
		isLoading: orgId ? isLoading : false,
		error: error ? (error instanceof Error ? error.message : 'Errore metriche predittive') : null,
	};
}
